import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { HeartPulse, MessageCircleHeart, Sparkles, Stethoscope, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  {
    icon: HeartPulse,
    tKey: "t1", dKey: "d1",
    tint: "bg-primary text-primary-foreground",
  },
  {
    icon: MessageCircleHeart,
    tKey: "t2", dKey: "d2", cKey: "c2",
    tint: "bg-primary/15 text-primary",
    to: "/app/companion",
  },
  {
    icon: Sparkles,
    tKey: "t3", dKey: "d3", cKey: "c3",
    tint: "bg-secondary text-secondary-foreground",
    to: "/app/checkin",
  },
  {
    icon: Stethoscope,
    tKey: "t4", dKey: "d4", cKey: "c4",
    tint: "bg-sage/15 text-sage",
    to: "/app/professionals",
  },
];

export function WelcomeTour({ open, onComplete }) {
  const { t } = useI18n();
  const [i, setI] = useState(0);
  const navigate = useNavigate();
  const step = STEPS[i];
  const isLast = i === STEPS.length - 1;
  const Icon = step.icon;

  const goTo = (path) => { onComplete(); navigate(path); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onComplete()}>
      <DialogContent className="max-w-md overflow-hidden rounded-3xl [&>button]:hidden" data-testid="welcome-dialog">
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5" data-testid="welcome-progress">
            {STEPS.map((_, idx) => (
              <span key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-primary" : "w-1.5 bg-border"}`} />
            ))}
          </div>
          {!isLast && (
            <button onClick={onComplete} data-testid="welcome-skip-button" className="text-xs text-muted-foreground hover:text-foreground">
              {t("tour.skip")}
            </button>
          )}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.28 }}
            className="flex flex-col items-center py-4 text-center"
          >
            <div className={`mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ${step.tint} ${i === 0 ? "animate-breathe" : ""}`}>
              <Icon className="h-8 w-8" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-2xl">{t(`tour.${step.tKey}`)}</h2>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground">{t(`tour.${step.dKey}`)}</p>
            {step.to && (
              <button onClick={() => goTo(step.to)} data-testid="welcome-goto-button"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                {t(`tour.${step.cKey}`)} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-2 flex items-center justify-between gap-3">
          {i > 0 ? (
            <Button variant="ghost" className="rounded-full gap-1.5" onClick={() => setI((v) => v - 1)} data-testid="welcome-back-button">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("tour.back")}
            </Button>
          ) : <span />}
          {isLast ? (
            <Button className="rounded-full gap-1.5" onClick={onComplete} data-testid="welcome-start-button">
              {t("tour.start")} <Sparkles className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          ) : (
            <Button className="rounded-full gap-1.5" onClick={() => setI((v) => v + 1)} data-testid="welcome-next-button">
              {t("tour.next")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
